/**
 * useLandingTelemetry — Phase Y client-side landing funnel instrumentation.
 *
 * - Generates a `session_id` via `sessionStorage` (UUID). Same tab reuses it.
 * - Respects `navigator.doNotTrack === "1"` — never fires.
 * - IntersectionObserver-based section-impression tracking.
 * - Click-handler wrapping for CTA buttons.
 * - `throttled: 1/session` semantics via a client-side `firedSet` guard.
 */
import { useCallback, useEffect, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "coda.telemetry.session";
const FIRED_KEY   = "coda.telemetry.fired";

function readSessionId() {
    try {
        let id = sessionStorage.getItem(SESSION_KEY);
        if (!id) {
            id = (crypto?.randomUUID?.() || `s_${Math.random().toString(36).slice(2)}_${Date.now()}`);
            sessionStorage.setItem(SESSION_KEY, id);
        }
        return id;
    } catch { return `s_ephemeral_${Date.now()}`; }
}

function readFiredSet() {
    try {
        const raw = sessionStorage.getItem(FIRED_KEY);
        return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
}
function persistFiredSet(set) {
    try { sessionStorage.setItem(FIRED_KEY, JSON.stringify(Array.from(set))); } catch { /* ignore */ }
}

function dntEnabled() {
    try {
        return navigator?.doNotTrack === "1"
            || navigator?.msDoNotTrack === "1"
            || window?.doNotTrack === "1";
    } catch { return false; }
}

async function post(event, sessionId, meta) {
    if (dntEnabled()) return;
    try {
        await fetch(`${BACKEND_URL}/api/telemetry/event`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                event,
                session_id: sessionId,
                meta: meta || {},
                referrer: (document.referrer || "").slice(0, 200),
            }),
            keepalive: true,          // safe against page unload
        });
    } catch { /* swallow — telemetry is best-effort */ }
}

/**
 * Top-level hook returning `fire(event, meta)` + section-observer helpers.
 */
export function useLandingTelemetry() {
    const sessionIdRef = useRef(null);
    const firedSetRef  = useRef(null);
    if (!sessionIdRef.current) sessionIdRef.current = readSessionId();
    if (!firedSetRef.current)  firedSetRef.current  = readFiredSet();

    const fire = useCallback((event, meta) => {
        if (!event) return;
        post(event, sessionIdRef.current, meta);
    }, []);

    const fireOnce = useCallback((event, meta) => {
        if (!event) return;
        const key = `${event}`;
        if (firedSetRef.current.has(key)) return;
        firedSetRef.current.add(key);
        persistFiredSet(firedSetRef.current);
        post(event, sessionIdRef.current, meta);
    }, []);

    // Fire `landing_view` once per session on mount.
    useEffect(() => {
        fireOnce("landing_view");
    }, [fireOnce]);

    return { fire, fireOnce, sessionId: sessionIdRef.current };
}

/**
 * Attach an IntersectionObserver to a DOM ref. Fires `event` once per session
 * when the ref becomes visible.
 */
export function useImpressionObserver(ref, event, options = {}) {
    const { fireOnce } = useLandingTelemetry();
    useEffect(() => {
        if (!ref.current) return;
        const el = ref.current;
        const io = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    fireOnce(event);
                    io.disconnect();
                    break;
                }
            }
        }, { threshold: 0.3, ...options });
        io.observe(el);
        return () => io.disconnect();
    }, [ref, event, fireOnce, options]);
}

/**
 * useHeroEngagementBeacon — Phase v5.3 §9.4 attention telemetry.
 *
 * Fires `landing_hero_engagement` **once per session** when the cursor
 * remains inside the target element continuously for `dwellMs` (default
 * 2000ms). Re-entering the hero after the first fire is a no-op.
 *
 * Contract:
 *   • `mouseenter` starts a dwell timer.
 *   • `mouseleave` cancels the timer (resets the debounce).
 *   • Timer completion → `fireOnce(landing_hero_engagement, {dwell_ms})`.
 *   • DNT respected transparently via the underlying `fireOnce` → `post`
 *     chain.
 *   • No listener on touch-only devices (`hover: none, pointer: coarse`
 *     — the beacon measures cursor attention, not scroll dwell, so a
 *     touchscreen has no meaningful signal to capture).
 */
export function useHeroEngagementBeacon(ref, { dwellMs = 2000 } = {}) {
    const { fireOnce } = useLandingTelemetry();
    useEffect(() => {
        if (!ref.current) return;
        // Skip touchscreen-only devices — no cursor to measure.
        try {
            const fine = window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches;
            if (!fine) return;
        } catch { /* older browsers — proceed anyway */ }
        const el = ref.current;
        let timer = 0;
        const onEnter = () => {
            if (timer) return;               // already counting
            timer = window.setTimeout(() => {
                timer = 0;
                fireOnce("landing_hero_engagement", { dwell_ms: dwellMs });
            }, dwellMs);
        };
        const onLeave = () => {
            if (timer) { clearTimeout(timer); timer = 0; }
        };
        el.addEventListener("mouseenter", onEnter);
        el.addEventListener("mouseleave", onLeave);
        return () => {
            if (timer) clearTimeout(timer);
            el.removeEventListener("mouseenter", onEnter);
            el.removeEventListener("mouseleave", onLeave);
        };
    }, [ref, dwellMs, fireOnce]);
}
